// @ts-nocheck

// Dependencies
import * as React from 'react';
import * as ReactDOM from 'react-dom';

// Components
import MediaQuery, { Devices } from '~/components/media-query';
// Providers
import { ServicesContext } from '~/providers/services-context';
// Services
import {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS } from
'~/services/actions/tracking-actions';

import Link from '../link';

// Styles
import styles from './style/menu.module.css';
import CustomDropdown from './custom-dropdown';
import MenuUnderliner from './menu-underliner';
import PromoCodeDropdown from './promo-code-dropdown';
import SplitNavLinks from './split-nav-links';

let measureText = null;

interface IMenuItem {
  isSelected?: boolean;
  text: string;
  url: string;
}

interface IProps {
  customId: unknown;
  menu: unknown;
}

interface IState {
  canEnrichList: false;
  fittingItemsQuantity: unknown;
}

class Menu extends React.Component<IProps, IState> {
  static contextType = ServicesContext;

  private listRef: React.RefObject<null>;
  private underlinerRef: React.RefObject<React.ReactInstance | null>;
  private currentItemRef: React.RefObject<null>;
  private moreButtonRef: React.RefObject<null>;
  private dropdownRef: React.RefObject<null>;

  constructor(props: IProps, state: IState) {
    super(props, state);

    this.listRef = React.createRef();
    this.underlinerRef = React.createRef();
    this.currentItemRef = React.createRef();
    this.moreButtonRef = React.createRef();
    this.dropdownRef = React.createRef();

    this.state = {
      fittingItemsQuantity: undefined
    };

    this.centerUnderlineBar = this.centerUnderlineBar.bind(this);
    this.initialise = this.initialise.bind(this);
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      // @ts-ignore
      import('measure-text').
      then(({ default: module }) => {
        // God knows why we have to double default here... but it works
        measureText = module.default;
      }).
      then(() => {
        this.initialise();
        window.addEventListener('resize', this.initialise);
        this.setState({ canEnrichList: true });
      });
    } else {
      this.initialise();
      window.addEventListener('resize', this.initialise);
      this.setState({ canEnrichList: true });
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.initialise);
    }
  }

  componentDidUpdate(prevProps) {
    this.centerUnderlineBar();

    if (prevProps.menu?.[0]?.url !== this.props.menu?.[0]?.url) {
      this.initialise();
    }
  }

  private initialise() {
    if (this.listRef?.current) {
      this.calculateFittingItems(
        this.getElementWidth(this.listRef.current),
        this.props.menu
      );
    }
  }

  private centerUnderlineBar(instantaneous = true) {
    if (this.underlinerRef) {
      let itemToUnderline = null;
      if (this.currentItemRef?.current !== null) {
        itemToUnderline = this.currentItemRef;
      } else {
        itemToUnderline = this.moreButtonRef?.current || null;
      }

      if (!itemToUnderline) {
        if (typeof this.underlinerRef.hide === 'function') {
          this.underlinerRef.hide();
        }
        return;
      }

      const currentItemDOMElement = itemToUnderline;
      const parentOffsetLeft = currentItemDOMElement?.parentElement?.offsetLeft;

      let dimensions = {} as any;
      dimensions = {
        left: currentItemDOMElement?.offsetLeft - parentOffsetLeft,
        width: currentItemDOMElement?.offsetWidth
      };

      // animate the bar
      requestAnimationFrame(() => {
        if (
        this.underlinerRef &&
        typeof this.underlinerRef.updateDimensions === 'function')
        {
          this.underlinerRef.updateDimensions(
            dimensions.left,
            dimensions.width,
            instantaneous
          );
        }
      });
    }
  }

  private getSelectedItem(itemList: IMenuItem[]) {
    const isItemSelected = itemList.find((item) => item.isSelected);

    return isItemSelected || this.getSelectedItemByUrl(itemList);
  }

  private getSelectedItemByUrl(itemList: IMenuItem[]) {
    if (typeof window === 'undefined') {
      return null;
    }

    let selectedItem: IMenuItem = null;
    const path = window.location.pathname.split('/').splice(2).join('/');

    itemList.forEach((item) => {
      // set the selected item if the url matches exactly
      // or get the last match that it's similar
      if (path === item.url) {
        selectedItem = item;
      }
    });

    return selectedItem;
  }

  private calculateFittingItems(wrapperWidth, itemsList = []) {
    const availableSpace = wrapperWidth;

    let usedSpace = 0;
    let currentItemIndex = 0;

    const minimumDropdownWidth =
    this.measureMenuItem(this.context.labelActions.get('MORE')) + 30 + 30;
    while (usedSpace <= availableSpace && currentItemIndex < itemsList.length) {
      const nextItemWidth =
      this.measureMenuItem(itemsList[currentItemIndex].text.toUpperCase()) +
      30 +
      8;

      if (usedSpace + minimumDropdownWidth >= availableSpace) {
        break;
      }

      if (usedSpace + minimumDropdownWidth + nextItemWidth >= availableSpace) {
        break;
      } else {
        usedSpace += nextItemWidth;
        currentItemIndex++;
      }
    }

    this.setState({
      fittingItemsQuantity: currentItemIndex + 1
    });
  }

  private getElementWidth(ref: HTMLDivElement): number {
    return ref.offsetWidth;
  }

  private measureMenuItem(itemText: string): number {
    const measurement = measureText({
      text: itemText,
      fontFamily: 'Public Sans',
      fontSize: '15px',
      lineHeight: 1,
      fontWeight: 300,
      fontStyle: 'normal'
    });

    return measurement.width.value;
  }

  private renderDropdown(dropdownItems = [], dropdownLabel): JSX.Element {
    if (!dropdownItems?.length || !dropdownLabel) {
      return null;
    }

    const isSplitDropdown = dropdownItems.every(
      ({ subCategories }) => !!subCategories
    );

    const isPromoCodesDropdown = dropdownItems.every(
      (test) => test?.icon?.length === 2
    );

    const menuLinks = () => {
      if (isSplitDropdown) {
        return <SplitNavLinks links={dropdownItems} />;
      }

      if (isPromoCodesDropdown) {
        const trackMenu = (label: string): void => {
          if (label) {
            this.context.trackingActions.eventTracking({
              event: TRACKING_EVENTS.HAMBURGER_MENU_ITEM,
              eventCategory: TRACKING_CATEGORIES.NAVIGATION,
              eventAction:
              TRACKING_ACTIONS[TRACKING_EVENTS.HAMBURGER_MENU_ITEM],
              eventLabel: label
            });
          }
        };

        return (
          <PromoCodeDropdown
            callback={trackMenu}
            label="desktop - Promo Codes"
            links={dropdownItems} />);


      }

      const linksWithoutNestedDropdowns = dropdownItems.filter(
        ({ subCategories }) => !subCategories
      );
      const links = this.renderMenu(
        linksWithoutNestedDropdowns,
        linksWithoutNestedDropdowns.length,
        false
      );

      return <div className={styles.moreNavLinks}>{links}</div>;
    };

    return (
      <CustomDropdown
        key={dropdownLabel}
        label={dropdownLabel}
        dropdownRef={this.dropdownRef}>

				{menuLinks()}
			</CustomDropdown>);

  }

  private renderMenuItem(item, setRef): JSX.Element {
    if (!item) {
      return <div className={styles.emptyItem} />;
    }

    if (item?.subCategories?.length) {
      return this.renderDropdown(item.subCategories, item.text);
    }

    return (
      <div
        className={styles.linkWrapper}
        key={item.url}
        ref={(e) => setRef && item.isSelected ? this.currentItemRef = e : ''}>

				<Link
          className={`${styles.item} ${item.isSelected ? styles.selected : ''}`}
          to={item.url}>

					{item.text}
				</Link>
			</div>);

  }

  private enrichList(list: IMenuItem[]) {
    return list.map((item) => {
      const newItem = { ...item };
      newItem.isSelected = !!item.isSelected;
      return newItem;
    });
  }

  private renderMenu(
  itemList: IMenuItem[],
  menuLength: number,
  setRef: boolean)
  : Array<JSX.Element> {
    const { canEnrichList } = this.state;

    const list = canEnrichList ? this.enrichList(itemList) : itemList;
    const menuList = list.slice(0, menuLength);
    const currentItem: IMenuItem = this.getSelectedItem(menuList);

    return menuList.map((item) => {
      const enrichedItem = {
        ...item,
        isSelected: currentItem?.url === item?.url
      };

      return this.renderMenuItem(canEnrichList ? enrichedItem : item, setRef);
    });
  }

  render(): JSX.Element {
    const { customId, menu } = this.props;
    if (!menu || !menu.length) {
      return null;
    }

    const { fittingItemsQuantity } = this.state;
    const dropdownLabel = this.context.labelActions.get('MORE');
    const dropdown =
    menu.length > fittingItemsQuantity ?
    this.renderDropdown(menu, dropdownLabel) :
    null;

    return (
      <>
				<div
          id={customId}
          data-testid="menu-wrapper"
          className={styles.wrapper}>

					<MediaQuery renderOnMismatch>
						{(device) => {
              // Setting isDesktop to false here will set menuLength to be equal to menu.length
              // Otherwise it would try and calculate how many menu items to display in the viewport, which would be problematic when SSR
              const isDesktop = device === Devices.desktop;
              const isBrowser = typeof window !== 'undefined';

              const menuLength =
              isDesktop && isBrowser ? fittingItemsQuantity : menu.length;
              const menuItems = this.renderMenu(menu, menuLength, true);

              return (
                <div className={styles.list} ref={this.listRef}>
									{menuItems}
									{isDesktop && isBrowser ? dropdown : null}
								</div>);

            }}
					</MediaQuery>
					<MenuUnderliner
            onRef={(ref) => this.underlinerRef = ref}
            extendClass={styles.underliner} />

				</div>
				<div className={styles.dropdownWrapper} ref={this.dropdownRef} />
			</>);

  }
}

export default Menu;